<template>
  <div>
    <error-message-dialog
      :opened.sync="errorMessageDialogOpened"
      :message="errorMessage"
    />

    <review-signature-request-dialog
      :opened.sync="reviewSignatureRequestDialogOpened"
      :currentCompany="currentCompany"
      :signatureRequest="currentSignatureRequest"
      @refresh-table="refreshTable"
      @refresh-table-until-sent="refreshTableUntilSent"
      @download-document="downloadDocument"
      @download-signed-pages-only="downloadSignedPagesOnly"
    />

    <upload-signature-request-dialog
      :opened.sync="uploadSignatureRequestDialogOpened"
      :currentCompany="currentCompany"
      :hideEntitySelect="true"
      :selectedYear="
        currentQuestionnaire ? currentQuestionnaire.tax_year : currentYear
      "
      :currentQuestionnaire="currentQuestionnaire"
      :sectionId="currentSectionId"
      @refresh-table="refreshTableWithPolling"
    />

    <signature-request-in-process-dialog
      :opened.sync="signatureRequestInProcessDialogOpened"
      @cancelProcessing="cancelProcessing"
    />

    <v-card class="content-container mt-4">
      <v-card-title
        class="py-2"
        style="background-color: #f7f7f7"
        v-if="currentSectionId"
      >
        <v-layout align-center class="px-2">
          <span class="subheading font-weight-bold"> Deliver Tax Return </span>
          <v-spacer />
          <v-tooltip bottom>
            <v-btn
              color="primary"
              slot="activator"
              @click="uploadSignatureRequestDialogOpened = true"
              class="default-input text-none"
              :disabled="
                currentQuestionnaire.locked || signatureRequests.length > 0
              "
            >
              <v-icon class="mr-2">upload</v-icon>
              Upload Tax Return
            </v-btn>
            <span v-if="currentQuestionnaire.locked"
              >Please unlock the questionnaire</span
            >
            <span v-else-if="signatureRequests.length > 0"
              >A tax return has been uploaded.</span
            >
            <span v-else
              >Uploading a tax return will start the delivery process</span
            >
          </v-tooltip>

          <soraban-action-button
            @click="refreshTable()"
            icon="refresh"
            help="Refresh Table"
            class="success--text"
          />
        </v-layout>
      </v-card-title>

      <v-card-title v-else>
        <span class="subheading font-weight-bold"> Signature Requests </span>
        <v-spacer />
        <span class="mr-2">Year</span>
        <div style="width: 100px" class="mr-2">
          <v-select
            v-model="currentYear"
            :items="yearOptionsWithAll"
            hide-details
            class="default-input body-1"
            solo
            small
          />
        </div>

        <soraban-action-button
          @click="refreshTable()"
          icon="refresh"
          help="Refresh Table"
          class="success--text"
        />

        <soraban-action-button
          @click="showUploadSignatureDialog()"
          icon="upload"
          help="Upload document for signatures"
        />
      </v-card-title>

      <v-divider />

      <v-card-text>
        <div class="mb-4">
          <v-data-table
            :headers="headers"
            :items="signatureRequests"
            class="project-table text-xs-left"
            :pagination.sync="pagination"
            :rows-per-page-items="pagination.options"
            :loading="tableLoading"
            :class="
              $vuetify.breakpoint.mdAndUp
                ? 'overview-data-table'
                : 'mobile-overview-data-table'
            "
          >
            <template slot="items" slot-scope="props">
              <tr :key="props.index" slot="header">
                <td>
                  <v-layout
                    align-center
                    @click="viewSignatureRequest(props.item)"
                    style="cursor: pointer"
                  >
                    {{ getName(props.item) }}
                  </v-layout>
                </td>

                <td class="text-xs-left">
                  <v-layout align-center>
                    <v-progress-circular
                      v-if="props.item.status === 'processing'"
                      indeterminate
                      color="primary"
                      size="24"
                      class="mr-2"
                    />
                    <i
                      v-else
                      class="material-icons-outlined mr-2"
                      :class="getSignatureStatus(props.item).class"
                    >
                      {{ getSignatureStatus(props.item).icon }}
                    </i>

                    <span class="font-weight-bold">{{
                      getSignatureStatus(props.item).text
                    }}</span>
                  </v-layout>
                </td>

                <td class="text-xs-left">
                  <span>{{ moment(props.item.updated_at).fromNow() }}</span>
                </td>

                <td>
                  <v-layout align-center justify-center>
                    <v-menu
                      transition="slide-y-transition"
                      bottom
                      left
                      nudge-bottom="30"
                      class="my-1"
                    >
                      <v-icon slot="activator" color="#0a1a25">
                        more_vert
                      </v-icon>

                      <v-list>
                        <v-list-tile @click="downloadDocument(props.item)">
                          <v-list-tile-title>
                            Download (all pages)
                          </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="props.item.signed_pages_only !== null"
                          @click="downloadSignedPagesOnly(props.item)"
                        >
                          <v-list-tile-title>
                            Download (signed pages only)
                          </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="props.item.status === 'sent'"
                          @click="resend(props.item)"
                        >
                          <v-list-tile-title> Resend </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile
                          v-if="props.item.status !== 'completed'"
                          @click="markAsCompleted(props.item)"
                        >
                          <v-list-tile-title>
                            Mark as completed
                          </v-list-tile-title>
                        </v-list-tile>

                        <v-list-tile @click="remove(props.item)">
                          <v-list-tile-title> Remove </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </v-layout>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReviewSignatureRequestDialog from '@/components/dialogs/ReviewSignatureRequestDialog';
import UploadSignatureRequestDialog from '@/components/dialogs/UploadSignatureRequestDialog';
import SignatureRequestInProcessDialog from '@/components/dialogs/SignatureRequestInProcessDialog';
import API from '@/api';
import moment from 'moment';
import SignatureRequestsMixin from '@/mixins/SignatureRequestsMixin';
import ErrorMessageDialog from '@/components/dialogs/ErrorMessageDialog';
import { mapGetters } from 'vuex';
import { yearOptions } from '@/constants/variables';

export default {
  components: {
    ReviewSignatureRequestDialog,
    UploadSignatureRequestDialog,
    SignatureRequestInProcessDialog,
    ErrorMessageDialog,
  },

  props: {
    currentCompany: {
      type: Object,
    },

    selectedYear: {
      optional: true,
    },

    currentQuestionnaire: {
      type: Object,
      optional: true,
    },

    currentSectionId: {
      type: Number,
      optional: true,
    },
  },

  mixins: [SignatureRequestsMixin],

  data() {
    return {
      currentYear: 'All',
      tableLoading: false,
      headers: [
        {
          text: 'Name (Click to view)',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '30%',
        },
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'status',
          width: '17%',
        },
        {
          text: 'Last Activity',
          align: 'left',
          width: '15%',
          value: 'updated_at',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '7%',
        },
      ],

      pagination: {
        descending: true,
        rowsPerPage: 25,
      },

      signatureStatus: 'All',

      signatureRequestInProcessDialogOpened: false,
      reviewSignatureRequestDialogOpened: false,
      uploadSignatureRequestDialogOpened: false,

      errorMessageDialogOpened: false,
      errorMessage: '',

      signatureRequests: [],
      currentSignatureRequest: null,

      isPolling: false,
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),

    yearOptionsWithAll() {
      return ['All', ...yearOptions.map(c => c.toString())];
    },

    currentCompanyId() {
      return this.currentCompany.id;
    },
  },

  methods: {
    moment,
    showUploadSignatureDialog() {
      if (this.currentCompany.inactive) {
        this.errorMessageDialogOpened = true;
        this.errorMessage =
          'Unable to send signature requests to an inactive entity. Please reactivate the entity first.';
      } else {
        this.uploadSignatureRequestDialogOpened = true;
      }
    },

    async refreshTable() {
      try {
        this.tableLoading = true;
        const response = await this.getSignatureRequestsByCompanyId();

        this.signatureRequests = response.data;

        if (
          this.signatureRequests.some(
            request => request.status === 'processing',
          )
        ) {
          this.refreshTableUntilAllProcessed();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.tableLoading = false;
      }
    },

    async refreshTableWithPolling(documentLocation, taxYear) {
      if (
        this.currentYear !== 'All' &&
        parseInt(taxYear) !== parseInt(this.currentYear)
      ) {
        return;
      }

      let refreshInterval;

      const refreshTable = async () => {
        this.tableLoading = true;
        try {
          const response = await this.getSignatureRequestsByCompanyId();

          this.signatureRequests = response.data;

          if (
            this.signatureRequests.some(
              request => request.source === documentLocation,
            )
          ) {
            clearInterval(refreshInterval);
            this.tableLoading = false;

            this.refreshTableUntilAllProcessed();
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async refreshTableUntilAllProcessed() {
      if (this.isPolling) {
        return;
      }

      let refreshInterval;
      this.isPolling = true;

      const refreshTable = async () => {
        try {
          const response = await this.getSignatureRequestsByCompanyId();

          this.signatureRequests = response.data;

          if (
            !this.signatureRequests.some(
              request => request.status === 'processing',
            )
          ) {
            clearInterval(refreshInterval);
            this.isPolling = false;
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async refreshTableUntilSent(signatureRequestId) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          this.tableLoading = true;
          const response = await this.getSignatureRequestsByCompanyId();

          this.signatureRequests = response.data;
          const signatureRequest = this.signatureRequests.find(
            req => req.id === signatureRequestId,
          );

          if (signatureRequest && signatureRequest.status === 'sent') {
            clearInterval(refreshInterval);
            this.tableLoading = false;
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    viewSignatureRequest(item) {
      if (item.status === 'processing') {
        this.currentSignatureRequest = item;
        this.signatureRequestInProcessDialogOpened = true;
      } else {
        this.currentSignatureRequest = item;
        this.reviewSignatureRequestDialogOpened = true;
      }
    },

    getSignatureRequestsByCompanyId() {
      if (this.currentCompanyId) {
        return API.signatureRequest.getSignatureRequestsByCompanyId({
          companyId: this.currentCompanyId,
          year: this.currentYear === 'All' ? null : this.currentYear,
          sectionId: this.currentSectionId,
        });
      }
    },

    async cancelProcessing() {
      try {
        this.tableLoading = true;
        this.signatureRequestInProcessDialogOpened = false;

        await API.signatureRequest.cancelProcessing({
          id: this.currentSignatureRequest.id,
          companyId: this.currentCompany.id,
        });

        await this.refreshTable();
      } finally {
        this.tableLoading = false;
      }
    },
  },

  watch: {
    async selectedYear() {
      this.currentYear = this.selectedYear;
    },

    async currentYear() {
      await this.refreshTable();
    },

    async currentCompanyId() {
      await this.refreshTable();
    },
  },

  async mounted() {
    await this.refreshTable();
  },

  async created() {
    await this.refreshTable();

    if (this.$route.query.signatureRequestId) {
      const signatureRequest = this.signatureRequests.find(
        s => s.id === parseInt(this.$route.query.signatureRequestId),
      );

      if (signatureRequest) {
        this.viewSignatureRequest(signatureRequest);
      }
    }
  },
};
</script>

<style lang="postcss" scoped></style>
